import apiService from './api';
import {
  Ticket,
  CreateTicketData,
  UpdateTicketData,
  TicketFilters,
  TicketStats,
  Comment,
} from '@/types/ticket';
import { PaginatedResponse, PaginationParams } from '@/types/common';

class TicketService {
  async getTickets(
    projectId: string,
    params?: PaginationParams & TicketFilters
  ): Promise<PaginatedResponse<Ticket>> {
    const searchParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {

      // Handle multiselect arrays
      if (Array.isArray(value)) {
        value.forEach((v) => {
          searchParams.append(key, String(v));
        });
      }

      // Handle normal fields
      else if (
        value !== undefined &&
        value !== null &&
        value !== ''
      ) {
        searchParams.append(key, String(value));
      }
    });

    console.log(searchParams.toString());

    return apiService.get<PaginatedResponse<Ticket>>(
      `/projects/${projectId}/tickets?${searchParams.toString()}`
    );
  }

  async getTicket(id: string): Promise<Ticket> {
    return apiService.get<Ticket>(`/tickets/${id}`);
  }

  async createTicket(projectId: string, data: CreateTicketData): Promise<Ticket> {
    return apiService.post<Ticket>(`/projects/${projectId}/tickets`, data);
  }

  async updateTicket(id: string, data: UpdateTicketData): Promise<Ticket> {
    return apiService.patch<Ticket>(`/tickets/${id}`, data);
  }

  async deleteTicket(id: string): Promise<void> {
    return apiService.delete<void>(`/tickets/${id}`);
  }

  async assignTicket(id: string, assigneeId: string | null): Promise<Ticket> {
    return apiService.patch<Ticket>(`/tickets/${id}`, { assignee_id: assigneeId });
  }

  async getTicketStats(projectId: string, filters?: TicketFilters): Promise<TicketStats> {
    return apiService.get<TicketStats>(`/projects/${projectId}/stats`, { params: filters });
  }

  // Comments
  async getTicketComments(ticketId: string): Promise<Comment[]> {
    return apiService.get<Comment[]>(`/comments/ticket/${ticketId}/comments`);
  }

  async createComment(
    ticketId: string,
    data: {
      content: string;
      isInternal?: boolean;
    }
  ): Promise<Comment> {
    return apiService.post<Comment>(`/tickets/${ticketId}/comments`, data);
  }

  async updateComment(
    commentId: string,
    data: { content: string }
  ): Promise<Comment> {
    return apiService.patch<Comment>(`/comments/${commentId}`, data);
  }

  async deleteComment(commentId: string): Promise<void> {
    return apiService.delete<void>(`/comments/${commentId}`);
  }

  // Attachments
  async uploadAttachment(
    ticketId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return apiService.post<{ url: string; filename: string }>(
      `/tickets/${ticketId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );
  }

  async deleteAttachment(ticketId: string, attachmentId: string): Promise<void> {
    return apiService.delete<void>(
      `/tickets/${ticketId}/attachments/${attachmentId}`
    );
  }
}

export const ticketService = new TicketService();
export default ticketService;
